import { Camera, Mail, PlaySquare } from "lucide-react";
import type { FormEvent } from "react";
import { useState } from "react";
import { brand } from "../config/brand";
import { newsletter } from "../lib/api";
import { SmartLink } from "./SmartLink";

type FooterLink = {
  label: string;
  href: string;
};

const footerColumns: Array<{ title: string; links: FooterLink[] }> = [
  {
    title: "Menus",
    links: [
      { label: "Today's Menu", href: "/menus" },
      { label: "Brunch", href: "/menus?category=brunch" },
      { label: "Dinner Plates", href: "/menus?category=dinner" },
      { label: "Cocktails", href: "/private-chef#book" },
      { label: "Desserts", href: "/menus?category=dessert" }
    ]
  },
  {
    title: "Private Chef",
    links: [
      { label: "In-Home Dining", href: "/private-chef#in-home-dining" },
      { label: "Events & Celebrations", href: "/events#celebrations" },
      { label: "How It Works", href: "/private-chef#how-it-works" },
      { label: "Book a Chef", href: "/private-chef#book" }
    ]
  },
  {
    title: "Recipes",
    links: [
      { label: "All Recipes", href: "/recipes" },
      { label: "Videos", href: "/recipes#videos" },
      { label: "Baking", href: "/recipes?search=bread" },
      { label: "Cocktails", href: "/recipes?search=cocktail" }
    ]
  },
  {
    title: "About",
    links: [
      { label: "Our Story", href: "/our-story" },
      { label: "Gallery", href: brand.instagramUrl },
      { label: "Press", href: "/press" },
      { label: "Contact", href: "/contact" }
    ]
  }
];

export function Footer() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setStatus("sending");
    try {
      await newsletter(String(form.get("email")));
      event.currentTarget.reset();
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  return (
    <footer className="site-footer">
      <section className="newsletter-band" id="newsletter">
        <div className="stamp">OJ's Bistro<br />Cook Gather Enjoy</div>
        <div>
          <h2>Let's stay in touch</h2>
          <p>Get new recipes, menus, and exclusive invites delivered straight to your inbox.</p>
        </div>
        <form onSubmit={handleSubmit}>
          <input type="email" name="email" placeholder="Your email address" required />
          <button type="submit" disabled={status === "sending"}>
            {status === "sending" ? "Sending..." : "Sign me up"}
          </button>
          {status === "success" ? <p className="form-note">You're on the list.</p> : null}
          {status === "error" ? <p className="form-note form-note-error">Signup did not go through. Try again.</p> : null}
        </form>
      </section>
      <section className="footer-links" id="contact">
        <div className="footer-brand">
          <img src={brand.logo} alt={brand.markAlt} />
          <p>{brand.tagline} Private experiences that bring people together.</p>
          <div className="social-row">
            <SmartLink href={brand.instagramUrl} aria-label="OJ's Bistro Instagram">
              <Camera size={18} />
            </SmartLink>
            <SmartLink href="https://www.youtube.com/" aria-label="OJ's Bistro YouTube">
              <PlaySquare size={18} />
            </SmartLink>
            <SmartLink href={`mailto:${brand.email}`} aria-label="Email OJ's Bistro">
              <Mail size={18} />
            </SmartLink>
          </div>
        </div>
        {footerColumns.map((column) => (
          <FooterColumn key={column.title} title={column.title} links={column.links} />
        ))}
      </section>
      <div className="legal-row">
        <span>© 2026 OJ's Bistro. All rights reserved.</span>
        <SmartLink href="/privacy">Privacy Policy</SmartLink>
        <SmartLink href="/terms">Terms & Conditions</SmartLink>
      </div>
    </footer>
  );
}

function FooterColumn({ title, links }: { title: string; links: FooterLink[] }) {
  return (
    <div>
      <h3>{title}</h3>
      {links.map((link) => (
        <SmartLink key={link.label} href={link.href}>
          {link.label}
        </SmartLink>
      ))}
    </div>
  );
}
